/**
 * Recall Check — Mismatch Watchlist (master admin, global).
 *
 * Every amber caused by a make/model spelling we can't reconcile is logged with
 * its raw strings. Adding a normalization rule here turns that false-amber into
 * a real green or red — i.e. a recovered lead — on all three surfaces at once.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Loader2, RefreshCw, Wrench, CheckCircle2, TriangleAlert } from "lucide-react";

const INPUT =
  "w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-[12.5px] text-gray-900 outline-none focus:border-gray-900";

export default function RecallMismatchWatchlist() {
  const { apiFetch } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row id
  const [draft, setDraft] = useState({ alias_make: "", alias_model: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/admin/recall/mismatches?limit=50", { method: "GET" });
      setData(r?.data || null);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const openRule = (row) => {
    setEditing(row.id);
    setDraft({ alias_make: row.make, alias_model: row.model });
  };

  const saveRule = async (row) => {
    if (!draft.alias_model.trim()) {
      toast?.({ title: "Enter the spelling NHTSA uses", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/admin/recall/aliases", {
        method: "POST",
        data: {
          make: row.make,
          model: row.model,
          model_year: row.model_year,
          alias_make: draft.alias_make.trim() || row.make,
          alias_model: draft.alias_model.trim(),
        },
      });
      toast?.({ title: `Rule added — ${row.make} ${row.model} → ${draft.alias_model.trim()}` });
      setEditing(null);
      await load();
    } catch (e) {
      toast?.({ title: String(e?.response?.data?.detail || "Could not save rule"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-2 text-gray-500 text-[13px]">
        <Loader2 className="animate-spin" size={15} /> Loading mismatch watchlist…
      </div>
    );
  }

  const rows = data?.rows || [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="recall-mismatch-watchlist">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
            <Wrench size={15} className="text-gray-400" /> Mismatch watchlist
            <span className="text-[11px] font-medium text-gray-400">(all dealers)</span>
          </h3>
          <p className="text-[12px] text-gray-500 mt-1 max-w-2xl">
            Vehicles we had to show as amber because NHTSA&apos;s spelling didn&apos;t match the
            decoded make/model. Add the spelling NHTSA uses and the next lookup returns a real
            green or red instead — on every surface.
          </p>
        </div>
        <button
          type="button" onClick={load}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-[12.5px] hover:bg-gray-50"
          data-testid="recall-watchlist-refresh"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="flex gap-6 mt-4 mb-1">
        <Stat label="Open spellings" value={data?.open_count ?? 0} testId="recall-watchlist-open-count" />
        <Stat label="Lookups affected" value={data?.affected_lookups ?? 0} testId="recall-watchlist-affected" />
      </div>

      {rows.length === 0 ? (
        <div className="mt-3 text-[12.5px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2.5 flex items-center gap-2">
          <CheckCircle2 size={14} /> No unresolved mismatches. Every lookup is resolving to a
          real result.
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="recall-watchlist-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3">Decoded as</th>
                <th className="py-2 pr-3">Year</th>
                <th className="py-2 pr-3">Lookups</th>
                <th className="py-2 pr-3">Spellings tried</th>
                <th className="py-2">Normalization rule</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 align-top" data-testid={`recall-watchlist-row-${r.id}`}>
                  <td className="py-2.5 pr-3 font-medium text-gray-900">
                    {r.make} {r.model}
                  </td>
                  <td className="py-2.5 pr-3 text-gray-600">{r.model_year}</td>
                  <td className="py-2.5 pr-3">
                    <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                      <TriangleAlert size={12} /> {r.occurrences}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-[11px] text-gray-500 max-w-[220px] truncate" title={r.tried_spellings || ""}>
                    {r.tried_spellings || "—"}
                  </td>
                  <td className="py-2.5">
                    {editing === r.id ? (
                      <div className="flex gap-2 items-center">
                        <input
                          className={INPUT} style={{ width: 130 }} value={draft.alias_make}
                          onChange={(e) => setDraft((d) => ({ ...d, alias_make: e.target.value }))}
                          placeholder="Make" data-testid="recall-alias-make"
                        />
                        <input
                          className={INPUT} style={{ width: 150 }} value={draft.alias_model}
                          onChange={(e) => setDraft((d) => ({ ...d, alias_model: e.target.value }))}
                          placeholder="NHTSA model e.g. 328i" data-testid="recall-alias-model"
                        />
                        <button
                          type="button" onClick={() => saveRule(r)} disabled={saving}
                          className="rounded-md bg-gray-900 text-white px-3 py-1.5 text-[12px] font-semibold hover:bg-gray-800 disabled:opacity-60"
                          data-testid="recall-alias-save"
                        >
                          {saving ? "Saving…" : "Save"}
                        </button>
                        <button
                          type="button" onClick={() => setEditing(null)}
                          className="text-[12px] text-gray-500 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button" onClick={() => openRule(r)}
                        className="text-[12.5px] text-blue-700 hover:underline"
                        data-testid={`recall-add-rule-${r.id}`}
                      >
                        Add rule
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div>
      <div className="text-[20px] font-bold text-gray-900 leading-none" data-testid={testId}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  );
}
